import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CustodyReport,
  PaginatedResult,
  PaymentMethod,
  ReturnDisposition,
  ReturnableLine,
  SalesReturnDetail,
  SalesReturnStatus,
  SalesReturnSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';
import { inventoryKeys } from '@/modules/inventory/api/inventory.api';
import { salesKeys } from './sales.api';

export interface ReturnListParams {
  page?: number;
  pageSize?: number;
  status?: SalesReturnStatus | '';
  quarantinedOnly?: boolean;
  search?: string;
}

export const returnKeys = {
  all: [...salesKeys.all, 'returns'] as const,
  list: (params: ReturnListParams) => [...returnKeys.all, 'list', params] as const,
  detail: (id: string) => [...returnKeys.all, 'detail', id] as const,
  returnable: (orderId: string) => [...returnKeys.all, 'returnable', orderId] as const,
  custody: () => [...salesKeys.all, 'custody'] as const,
};

export function useReturnsQuery(params: ReturnListParams) {
  return useQuery({
    queryKey: returnKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<SalesReturnSummary>>('/sales/returns', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.status ? { status: params.status } : {}),
          ...(params.quarantinedOnly ? { quarantinedOnly: true } : {}),
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useReturnQuery(id: string | undefined) {
  return useQuery({
    queryKey: returnKeys.detail(id ?? ''),
    queryFn: () => api.get<SalesReturnDetail>(`/sales/returns/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useReturnableLinesQuery(orderId: string | undefined) {
  return useQuery({
    queryKey: returnKeys.returnable(orderId ?? ''),
    queryFn: () => api.get<ReturnableLine[]>(`/sales/orders/${orderId ?? ''}/returnable`),
    enabled: Boolean(orderId),
  });
}

/** Branch-scoped, so it waits for a branch rather than asking without one. */
export function useCustodyReportQuery(enabled = true) {
  return useQuery({
    queryKey: returnKeys.custody(),
    queryFn: () => api.get<CustodyReport>('/sales/custody'),
    enabled,
  });
}

/** A return moves goods as well as paperwork, so the stock caches go too. */
function useReturnInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: salesKeys.all });
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
  };
}

export interface CreateReturnPayload {
  lines: { orderLineId: string; quantity: number; notes?: string }[];
  reason?: string;
  notes?: string;
}

export function useCreateReturnMutation(orderId: string) {
  const invalidate = useReturnInvalidation();

  return useIdempotentMutation<SalesReturnDetail, CreateReturnPayload>(
    (payload, config) =>
      api.post<SalesReturnDetail>(`/sales/orders/${orderId}/returns`, payload, config),
    { onSuccess: invalidate },
  );
}

export function useInspectReturnLineMutation(returnId: string) {
  const invalidate = useReturnInvalidation();

  return useMutation({
    mutationFn: (payload: { lineId: string; disposition: ReturnDisposition; notes?: string }) =>
      api.post<SalesReturnDetail>(`/sales/returns/${returnId}/lines/${payload.lineId}/inspect`, {
        disposition: payload.disposition,
        ...(payload.notes ? { notes: payload.notes } : {}),
      }),
    onSuccess: invalidate,
  });
}

export function useCancelReturnMutation(returnId: string) {
  const invalidate = useReturnInvalidation();

  return useMutation({
    mutationFn: (reason: string) =>
      api.post<SalesReturnDetail>(`/sales/returns/${returnId}/cancel`, { reason }),
    onSuccess: invalidate,
  });
}

export interface RefundPayload {
  method: PaymentMethod;
  amountMinor: number;
  originalPaymentTransactionId?: string;
  reference?: string;
}

export function useRecordRefundMutation(orderId: string) {
  const invalidate = useReturnInvalidation();

  return useIdempotentMutation<{ id: string; warning: string | null }, RefundPayload>(
    (payload, config) =>
      api.post<{ id: string; warning: string | null }>(
        `/sales/orders/${orderId}/refunds`,
        payload,
        config,
      ),
    { onSuccess: invalidate },
  );
}
